import { jsx, jsxs } from "react/jsx-runtime";
import { c } from "react/compiler-runtime";
import { styled } from "@mui/material/styles";
import ComponentWrapper from "../ComponentWrapper/ComponentWrapper.js";
import GaugeComponent from "../Gauge/Gauge.js";
import Text from "../Text/Text.js";
import TrendWithIcon from "../Trend/WithIcon.js";
import Value from "../Value/Value.js";
import StackedBarChart from "../Charts/StackedBar.js";
import BarChart from "../Charts/Bar.js";
import BarTrendChart from "../Charts/BarTrend.js";
import MixedChart from "../Charts/Mixed.js";
import PieChart from "../Charts/Pie.js";
import LineChart from "../Charts/Line.js";
function shouldForwardProp(prop) {
  return !["withScroll"].includes(prop);
}
const Template = styled("div", {
  shouldForwardProp
})(({
  theme
}) => ({
  position: "relative",
  display: "flex",
  padding: theme.sizes.spacing["07"],
  flexDirection: "column",
  gap: theme.sizes.spacing["07"],
  justifyContent: "space-between",
  flex: "1 0 0",
  alignSelf: "stretch",
  ".card-container &": {
    padding: theme.sizes.spacing["07"]
    // minWidth: `${4 * theme.sizes.grid.width}rem`,
    // maxWidth: `${3.5 * theme.sizes.grid.width}rem`,
    // height: `${6.5 * theme.sizes.grid.height}rem`,
  },
  "& .text-and-value": {
    position: "relative",
    gap: theme.sizes.spacing["02"]
  },
  "& .template-primary-slot": {
    display: "flex",
    flexDirection: "row",
    gap: theme.sizes.spacing["05"],
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    "& .template-metric": {
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-start",
      width: "100%"
    },
    "& .template-metric .MuiTypography-root": {
      fontFeatureSettings: "'liga' off, 'clig' off",
      fontFamily: theme.fonts.family.number,
      fontSize: theme.fonts.size["2xl"],
      fontStyle: "normal",
      fontWeight: theme.fonts.weight.normal,
      lineHeight: theme.fonts.lineHeight["leading-7"],
      letterSpacing: theme.fonts.letterSpacing.normal
    },
    "& .template-metric.value .MuiTypography-root": {
      color: theme.colors.text.primary
    }
  },
  "& .template-secondary-slot": {
    display: "flex",
    flexDirection: "row",
    gap: theme.sizes.spacing["05"],
    height: "3rem",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    "& .template-metric .MuiTypography-root": {
      fontFamily: theme.fonts.family.number,
      fontSize: theme.fonts.size.sm,
      fontStyle: "normal",
      fontWeight: theme.fonts.weight.normal,
      lineHeight: theme.fonts.lineHeight["leading-5"],
      letterSpacing: theme.fonts.letterSpacing.normal
    },
    "& .template-metric.value .MuiTypography-root": {
      color: theme.colors.text.primary
    }
  },
  "& .text-and-value .text-with-help": {
    "& .MuiTypography-h6, & .MuiTypography-h5, & .MuiTypography-heading-md, & .MuiTypography-heading-sm": {
      color: theme.colors.text.tertiary
    }
  },
  "& .bar-chart, & .line-chart, & .gauge-chart, & .stacked-bar-chart, & .mixed-chart, & .pie-chart": {
    alignItems: "center",
    display: "flex",
    flexDirection: "column",
    "&.gauge-chart": {
      justifyContent: "center"
    }
  },
  "& .card-footer": {
    bottom: `-${theme.sizes.spacing["03"]}`
  },
  "& .goal-edit-slot": {
    position: "absolute",
    top: theme.sizes.spacing["04"],
    right: theme.sizes.spacing["05"],
    zIndex: 1
  }
}));
const ChartTemplate = (t0) => {
  const $ = c(82);
  const {
    title: t1,
    chart,
    footer: t2,
    isLoading: t3,
    colorScheme: t4,
    maxHeight,
    noComparison: t5,
    helpContent,
    style,
    goalEditSlot
  } = t0;
  const title = t1 === void 0 ? "" : t1;
  let t6;
  if ($[0] !== t2) {
    t6 = t2 === void 0 ? {
      text: "",
      isMarkdown: false
    } : t2;
    $[0] = t2;
    $[1] = t6;
  } else {
    t6 = $[1];
  }
  const footer = t6;
  const isLoading = t3 === void 0 ? false : t3;
  const colorScheme = t4 === void 0 ? "sand" : t4;
  const noComparison = t5 === void 0 ? false : t5;
  let t7;
  if ($[2] !== title) {
    t7 = typeof title === "object" && title !== null ? title : {
      text: title,
      help: null
    };
    $[2] = title;
    $[3] = t7;
  } else {
    t7 = $[3];
  }
  const {
    help: t8,
    text
  } = t7;
  const help = t8 === void 0 ? null : t8;
  let t9;
  if ($[4] !== chart) {
    t9 = chart !== void 0 ? chart.type !== "semi-circle" && chart.type !== "funnel" ? chart : {} : {};
    $[4] = chart;
    $[5] = t9;
  } else {
    t9 = $[5];
  }
  const {
    showLabels: t10,
    value: t11
  } = t9;
  const showLabels = t10 === void 0 ? false : t10;
  const value = t11 === void 0 ? null : t11;
  const trendOnTop = style === "trend-on-top";
  const labelFormat = style === "combined-label" || style === "value-label" ? style : void 0;
  const hasTrendFooter = chart && chart?.type !== "semi-circle" && chart?.type !== "funnel" && chart.trends && !noComparison && Array.isArray(chart.trends);
  const hasPrimarySlot = trendOnTop ? !!hasTrendFooter : !!value;
  const hasSecondarySlot = trendOnTop ? !!value : !!hasTrendFooter;
  const hasHeader = (text?.length ?? 0) > 0 || hasPrimarySlot;
  const hasHeaderValue = hasPrimarySlot && !!value && !trendOnTop;
  const hasHeaderTrends = hasPrimarySlot && !!hasTrendFooter && trendOnTop;
  const hasValueFooter = hasSecondarySlot && !!value && trendOnTop;
  const hasTrendInSecondary = hasSecondarySlot && !!hasTrendFooter && !trendOnTop;
  const hasGaugeFooter = chart && chart?.type === "semi-circle" && footer.text !== "";
  let chartHeight = (maxHeight ?? 10) * 2.5;
  if (hasHeader) {
    chartHeight = chartHeight - 2;
  }
  if (hasHeaderValue) {
    chartHeight = chartHeight - 2;
  }
  if (hasHeaderTrends) {
    chartHeight = chartHeight - 4;
  }
  if (hasTrendInSecondary) {
    chartHeight = chartHeight - 4;
  }
  if (hasValueFooter) {
    chartHeight = chartHeight - 2;
  }
  if (hasGaugeFooter) {
    chartHeight = chartHeight - 2.5;
  }
  const chartHeightInPixels = Math.max(chartHeight, 1) * 16;
  const headingVariant = chart?.type === "semi-circle" ? "heading-md" : "heading-sm";
  let trendsContent = null;
  if (hasTrendFooter && chart.trends) {
    let t122;
    if ($[6] !== chart.trends || $[7] !== isLoading) {
      let t132;
      if ($[9] !== isLoading) {
        t132 = (t142, idx) => {
          const {
            value: trendValue,
            comp,
            label,
            desiredTrend
          } = t142;
          return /* @__PURE__ */ jsxs(ComponentWrapper, { children: [
            /* @__PURE__ */ jsx(Text, { variant: "body-xs", text: label, help: null }),
            /* @__PURE__ */ jsx(ComponentWrapper, { className: "template-metric", children: /* @__PURE__ */ jsx(TrendWithIcon, { data: {
              original: trendValue,
              comparison: comp,
              desiredTrend: desiredTrend ?? "positive"
            }, isLoading }) })
          ] }, `trend-${idx}`);
        };
        $[9] = isLoading;
        $[10] = t132;
      } else {
        t132 = $[10];
      }
      t122 = chart.trends.map(t132);
      $[6] = chart.trends;
      $[7] = isLoading;
      $[8] = t122;
    } else {
      t122 = $[8];
    }
    trendsContent = t122;
  }
  let valueContent = null;
  if (value) {
    let t122;
    if ($[11] !== isLoading || $[12] !== value) {
      t122 = /* @__PURE__ */ jsx(ComponentWrapper, { className: "template-metric value", children: /* @__PURE__ */ jsx(Value, { data: value, isLoading, float: "left" }) });
      $[11] = isLoading;
      $[12] = value;
      $[13] = t122;
    } else {
      t122 = $[13];
    }
    valueContent = t122;
  }
  const primarySlotContent = trendOnTop ? trendsContent : valueContent;
  const secondarySlotContent = trendOnTop ? valueContent : trendsContent;
  let t12;
  if ($[14] !== chart?.type || $[15] !== goalEditSlot) {
    t12 = chart?.type === "semi-circle" && goalEditSlot && /* @__PURE__ */ jsx(ComponentWrapper, { className: "goal-edit-slot", children: goalEditSlot });
    $[14] = chart?.type;
    $[15] = goalEditSlot;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== hasHeader || $[18] !== hasPrimarySlot || $[19] !== headingVariant || $[20] !== help || $[21] !== helpContent || $[22] !== primarySlotContent || $[23] !== text) {
    t13 = hasHeader && /* @__PURE__ */ jsxs(ComponentWrapper, { className: "text-and-value", children: [
      text?.length > 0 && /* @__PURE__ */ jsx(ComponentWrapper, { className: "text-with-help", children: /* @__PURE__ */ jsx(Text, { variant: headingVariant, text: `${text}`, help, helpContent }) }),
      hasPrimarySlot && /* @__PURE__ */ jsx(ComponentWrapper, { className: "template-primary-slot", children: primarySlotContent })
    ] });
    $[17] = hasHeader;
    $[18] = hasPrimarySlot;
    $[19] = headingVariant;
    $[20] = help;
    $[21] = helpContent;
    $[22] = primarySlotContent;
    $[23] = text;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  if ($[25] !== chart || $[26] !== isLoading) {
    t14 = chart?.type === "semi-circle" && /* @__PURE__ */ jsx(ComponentWrapper, { className: "gauge-chart", children: /* @__PURE__ */ jsx(GaugeComponent, { data: {
      ...chart.data,
      goal: chart.goal?.goal || 0
    }, description: chart.description, isLoading, height: 140 }) });
    $[25] = chart;
    $[26] = isLoading;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  let t15;
  if ($[28] !== chart || $[29] !== chartHeightInPixels || $[30] !== colorScheme || $[31] !== isLoading || $[32] !== showLabels) {
    t15 = chart?.type === "stacked_bar_chart" && /* @__PURE__ */ jsx(ComponentWrapper, { className: ["stacked-bar-chart", !chart.trends || chart.trends.length === 0 ? "tall-chart" : ""].join(" "), children: /* @__PURE__ */ jsx(StackedBarChart, { title: "", isTransposedData: chart.configuration.transposed, colorScheme, dataset: chart.data, chartProps: chart, showLabels, isLoading, height: chartHeightInPixels }) });
    $[28] = chart;
    $[29] = chartHeightInPixels;
    $[30] = colorScheme;
    $[31] = isLoading;
    $[32] = showLabels;
    $[33] = t15;
  } else {
    t15 = $[33];
  }
  let t16;
  if ($[34] !== chart || $[35] !== chartHeightInPixels || $[36] !== colorScheme || $[37] !== isLoading || $[38] !== showLabels) {
    t16 = chart?.type === "bar_chart" && /* @__PURE__ */ jsx(ComponentWrapper, { className: ["bar-chart", !chart.trends || chart.trends.length === 0 ? "tall-chart" : ""].join(" "), children: /* @__PURE__ */ jsx(BarChart, { title: "", colorScheme, configuration: chart.configuration, series: chart.series, seriesData: chart.data, showLabels, isLoading, height: chartHeightInPixels }) });
    $[34] = chart;
    $[35] = chartHeightInPixels;
    $[36] = colorScheme;
    $[37] = isLoading;
    $[38] = showLabels;
    $[39] = t16;
  } else {
    t16 = $[39];
  }
  let t17;
  if ($[40] !== chart || $[41] !== chartHeightInPixels || $[42] !== isLoading || $[43] !== noComparison) {
    t17 = chart?.type === "funnel" && /* @__PURE__ */ jsx(ComponentWrapper, { className: "bar-trend-chart", children: /* @__PURE__ */ jsx(BarTrendChart, { title: "", configuration: chart.configuration, seriesData: chart.data, isLoading, height: chartHeightInPixels, noComparison }) });
    $[40] = chart;
    $[41] = chartHeightInPixels;
    $[42] = isLoading;
    $[43] = noComparison;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== chart || $[46] !== chartHeightInPixels || $[47] !== colorScheme || $[48] !== isLoading || $[49] !== showLabels) {
    t18 = chart?.type === "mixed_chart" && /* @__PURE__ */ jsx(ComponentWrapper, { className: ["mixed-chart", !chart.trends || chart.trends.length === 0 ? "tall-chart" : ""].join(" "), children: /* @__PURE__ */ jsx(MixedChart, { title: "", colorScheme, chartProps: chart, dataset: chart.data, showLabels, isLoading, height: chartHeightInPixels }) });
    $[45] = chart;
    $[46] = chartHeightInPixels;
    $[47] = colorScheme;
    $[48] = isLoading;
    $[49] = showLabels;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  let t19;
  if ($[51] !== chart || $[52] !== chartHeightInPixels || $[53] !== colorScheme || $[54] !== isLoading || $[55] !== labelFormat || $[56] !== showLabels) {
    t19 = chart?.type === "pie_chart" && /* @__PURE__ */ jsx(ComponentWrapper, { className: "pie-chart tall-chart", children: /* @__PURE__ */ jsx(PieChart, { title: "", colorScheme, chartProps: chart, dataset: chart.data, showLabels, isLoading, height: chartHeightInPixels, labelFormat }) });
    $[51] = chart;
    $[52] = chartHeightInPixels;
    $[53] = colorScheme;
    $[54] = isLoading;
    $[55] = labelFormat;
    $[56] = showLabels;
    $[57] = t19;
  } else {
    t19 = $[57];
  }
  let t20;
  if ($[58] !== chart || $[59] !== chartHeightInPixels || $[60] !== colorScheme || $[61] !== isLoading || $[62] !== showLabels) {
    t20 = chart?.type === "line_chart" && /* @__PURE__ */ jsx(ComponentWrapper, { className: ["line-chart", !chart.trends || chart.trends.length === 0 ? "tall-chart" : ""].join(" "), children: /* @__PURE__ */ jsx(LineChart, { colorScheme, series: chart.series, seriesData: chart.seriesData, xAxis: chart.xAxis, showLabels, isLoading, height: chartHeightInPixels }) });
    $[58] = chart;
    $[59] = chartHeightInPixels;
    $[60] = colorScheme;
    $[61] = isLoading;
    $[62] = showLabels;
    $[63] = t20;
  } else {
    t20 = $[63];
  }
  let t21;
  if ($[64] !== hasSecondarySlot || $[65] !== secondarySlotContent) {
    t21 = hasSecondarySlot && /* @__PURE__ */ jsx(ComponentWrapper, { className: "template-secondary-slot", children: secondarySlotContent });
    $[64] = hasSecondarySlot;
    $[65] = secondarySlotContent;
    $[66] = t21;
  } else {
    t21 = $[66];
  }
  let t22;
  if ($[67] !== chart?.type || $[68] !== footer) {
    t22 = chart?.type === "semi-circle" && footer.text !== "" && /* @__PURE__ */ jsx(Text, { variant: "body-xs", component: "div", ...footer, isFooter: true });
    $[67] = chart?.type;
    $[68] = footer;
    $[69] = t22;
  } else {
    t22 = $[69];
  }
  let t23;
  if ($[70] !== t12 || $[71] !== t13 || $[72] !== t14 || $[73] !== t15 || $[74] !== t16 || $[75] !== t17 || $[76] !== t18 || $[77] !== t19 || $[78] !== t20 || $[79] !== t21 || $[80] !== t22) {
    t23 = /* @__PURE__ */ jsxs(Template, { children: [
      t12,
      t13,
      t14,
      t15,
      t16,
      t17,
      t18,
      t19,
      t20,
      t21,
      t22
    ] });
    $[70] = t12;
    $[71] = t13;
    $[72] = t14;
    $[73] = t15;
    $[74] = t16;
    $[75] = t17;
    $[76] = t18;
    $[77] = t19;
    $[78] = t20;
    $[79] = t21;
    $[80] = t22;
    $[81] = t23;
  } else {
    t23 = $[81];
  }
  return t23;
};
export {
  ChartTemplate as default
};
